import type { ObservableSubscription } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { Column } from '@tanstack/react-table';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useCustomFieldsConfigContext } from '@work4all/data/lib/custom-fields';
import { IResponse } from '@work4all/data/lib/hooks/data-provider/useDataProvider';

import { SavedListFilter } from '@work4all/models/lib/Classes/SavedListFilter.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { getSavedFiltersCountsQuery } from '../utils';

type Props = {
  entity: Entities;
  columns: Column<unknown>[];
  skip: boolean;
  savedFilters: SavedListFilter[];
};

export const useSavedFiltersCounts = ({
  entity,
  savedFilters,
  columns,
  skip,
}: Props) => {
  const [result, setResult] = useState<Map<string, number>>(new Map());
  const subscriptionRef = useRef<ObservableSubscription | null>(null);

  const client = useApolloClient();
  const { customFields } = useCustomFieldsConfigContext();

  const query = useMemo(() => {
    if (skip || savedFilters.length === 0) {
      return null;
    }

    return getSavedFiltersCountsQuery({
      entity,
      columns,
      savedFilters,
      customFields,
    });
  }, [entity, columns, savedFilters, skip, customFields]);

  useEffect(() => {
    if (!query) {
      return;
    }

    const response = client.watchQuery<Record<string, IResponse>>({
      query,
      context: { batch: false },
    });

    subscriptionRef.current = response.subscribe((grouped) => {
      const newResult = new Map();

      if (grouped.loading) {
        return;
      }

      if (grouped.error || !grouped.data) {
        setResult(newResult);
        return;
      }

      for (const [key, value] of Object.entries(grouped.data)) {
        newResult.set(key.split('_')[1], value?.total ?? 0);
      }

      setResult(newResult);
    });

    return () => {
      subscriptionRef.current?.unsubscribe();
    };
  }, [query, client]);

  return result;
};
